import { create } from 'zustand';
import { fetchUserPreferences, updateUserPreferences } from '../api/client';
import {
  closePanelInLayout,
  createDefaultEditorLayout,
  migrateLegacyLocalLayout,
  moveColumnToSide,
  movePanelInLayout,
  normalizeEditorLayout,
  openPanelInLayout,
  parseEditorLayout,
  updateColumnPanelSizes,
  updateSideColumnSizes,
  type BottomPanelTab,
  type DockSide,
  type EditorLayoutV1,
  type EditorPanelId,
} from '../types/editorLayout';
import { useSimulationStore } from './simulationStore';

function syncBottomPanelFromLayout(layout: EditorLayoutV1) {
  useSimulationStore.getState().setBottomPanelOpen(layout.bottomOpen);
  useSimulationStore.getState().setBottomPanelTab(layout.bottomTab);
}

// ------------------------------------------------------------
// Editor dock layout state (persisted per user on server)
// ------------------------------------------------------------

const LOCAL_KEY = 'sim-designer-editor-layout-v1';
const DEBOUNCE_MS = 450;

interface EditorLayoutState {
  layout: EditorLayoutV1;
  hydrated: boolean;
  saving: boolean;

  hydrate: () => Promise<void>;
  setLayout: (layout: EditorLayoutV1) => void;
  setRootSizes: (sizes: number[]) => void;
  setCenterSizes: (sizes: number[]) => void;
  setBottomOpen: (open: boolean) => void;
  setBottomTab: (tab: BottomPanelTab) => void;
  movePanel: (
    panelId: EditorPanelId,
    targetSide: DockSide,
    targetColumnId: string | null,
    targetIndex: number,
  ) => void;
  moveColumn: (columnId: string, fromSide: DockSide, toSide: DockSide) => void;
  updateSideColumns: (side: DockSide, sizes: number[]) => void;
  updateColumnPanels: (
    side: DockSide,
    columnId: string,
    sizes: number[],
  ) => void;
  closePanel: (panelId: EditorPanelId) => void;
  openPanel: (panelId: EditorPanelId) => void;
  togglePanel: (panelId: EditorPanelId) => void;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

function loadLocalLayout(): EditorLayoutV1 | null {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return migrateLegacyLocalLayout();
    return parseEditorLayout(JSON.parse(raw));
  } catch {
    return migrateLegacyLocalLayout();
  }
}

function schedulePersist(layout: EditorLayoutV1) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(layout));
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
  void updateUserPreferences({ editor_layout: layout }).catch(() => {
      /* offline — local copy remains */
    });
  }, DEBOUNCE_MS);
}

function applyLayout(
  set: (partial: Partial<EditorLayoutState>) => void,
  layout: EditorLayoutV1,
) {
  const normalized = normalizeEditorLayout(layout);
  set({ layout: normalized });
  schedulePersist(normalized);
}

export const useEditorLayoutStore = create<EditorLayoutState>((set, get) => ({
  layout: createDefaultEditorLayout(),
  hydrated: false,
  saving: false,

  hydrate: async () => {
    if (get().hydrated) return;
    const local = loadLocalLayout();
    if (local) {
      set({ layout: local, hydrated: true });
      syncBottomPanelFromLayout(local);
    }
    try {
      const { preferences } = await fetchUserPreferences();
      const remote = parseEditorLayout(preferences.editor_layout);
      if (remote) {
        set({ layout: remote, hydrated: true });
        localStorage.setItem(LOCAL_KEY, JSON.stringify(remote));
        syncBottomPanelFromLayout(remote);
        return;
      }
    } catch {
      /* not logged in or offline */
    }
    set({ hydrated: true });
  },

  setLayout: (layout) => applyLayout(set, layout),

  setRootSizes: (sizes) => {
    if (sizes.length !== 3) return;
    const layout = { ...get().layout, rootSizes: sizes as [number, number, number] };
    applyLayout(set, layout);
  },

  setCenterSizes: (sizes) => {
    if (sizes.length !== 2) return;
    const layout = {
      ...get().layout,
      centerSizes: sizes as [number, number],
    };
    applyLayout(set, layout);
  },

  setBottomOpen: (open) => {
    syncBottomPanelFromLayout({ ...get().layout, bottomOpen: open });
    applyLayout(set, { ...get().layout, bottomOpen: open });
  },

  setBottomTab: (tab) => {
    applyLayout(set, { ...get().layout, bottomTab: tab });
  },

  movePanel: (panelId, targetSide, targetColumnId, targetIndex) => {
    applyLayout(
      set,
      movePanelInLayout(get().layout, panelId, targetSide, targetColumnId, targetIndex),
    );
  },

  moveColumn: (columnId, fromSide, toSide) => {
    applyLayout(set, moveColumnToSide(get().layout, columnId, fromSide, toSide));
  },

  updateSideColumns: (side, sizes) => {
    applyLayout(set, updateSideColumnSizes(get().layout, side, sizes));
  },

  updateColumnPanels: (side, columnId, sizes) => {
    applyLayout(set, updateColumnPanelSizes(get().layout, side, columnId, sizes));
  },

  closePanel: (panelId) => {
    applyLayout(set, closePanelInLayout(get().layout, panelId));
  },

  openPanel: (panelId) => {
    applyLayout(set, openPanelInLayout(get().layout, panelId));
  },

  togglePanel: (panelId) => {
    const layout = get().layout;
    const visible = layout.left.columns.some((c) => c.panels.includes(panelId))
      || layout.right.columns.some((c) => c.panels.includes(panelId));
    applyLayout(
      set,
      visible
        ? closePanelInLayout(layout, panelId)
        : openPanelInLayout(layout, panelId),
    );
  },
}));
